'use client';

import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { LucideIcon } from '../../icons/LucideIcon';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickChrome } from '../../lib/siteChromeCopy';
import { FormFieldControl } from '../forms/FormFieldControls';
import { SiteLeadForm } from '../forms/SiteLeadForm';
import type { ContactView } from './contactView';

export function ContactPicture({
  view,
  className,
  photoClass,
  sizes = SITE_IMAGE_SIZES.contactMap,
}: {
  view: ContactView;
  className: string;
  photoClass: string;
  sizes?: string;
}) {
  if (!view.showMap) return null;
  return (
    <div className={className}>
      {view.mapEmbedUrl ? (
        <iframe
          className={photoClass}
          src={view.mapEmbedUrl}
          title={view.sectionTitle || 'Map'}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      ) : view.mapImageSrc ? (
        <EditableMedia
          fieldKey="mapImage"
          editable={view.editable}
          activeEditTarget={view.target}
          className={photoClass}
          src={view.mapImageSrc}
          srcSet={view.mapImageSrcSet}
          sizes={sizes}
          alt={view.address || view.sectionTitle || 'Map'}
          width={1200}
          height={800}
          loading="lazy"
          decoding="async"
        />
      ) : view.editable ? (
        <button
          type="button"
          className={[
            photoClass,
            `${photoClass}--empty`,
            'ws-edit-media',
            view.target?.kind === 'media-field' && view.target.fieldKey === 'mapImage'
              ? 'ws-edit-child--active ws-edit-media--active'
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-edit-child="media-field"
          data-field-key="mapImage"
          aria-label={view.sectionTitle || 'Map photo'}
        />
      ) : (
        <div className={`${photoClass} ${photoClass}--empty`} aria-hidden />
      )}
    </div>
  );
}

export function ContactMessage({
  view,
  locale,
  className,
  titleClass,
  supportClass,
}: {
  view: ContactView;
  locale: string;
  className: string;
  titleClass: string;
  supportClass: string;
}) {
  const { editable, target } = view;
  return (
    <div className={className}>
      {view.formHeading || editable ? (
        <EditableContent
          as="h3"
          fieldKey="formHeading"
          editable={editable}
          activeEditTarget={target}
          className={titleClass}
        >
          {view.formHeading}
        </EditableContent>
      ) : null}
      {view.formSupporting || editable ? (
        <EditableContent
          fieldKey="formSupporting"
          editable={editable}
          activeEditTarget={target}
          className={supportClass}
        >
          {view.formSupporting}
        </EditableContent>
      ) : null}
      <SiteLeadForm locale={locale} formKey={view.formKey} editable={editable} className="ws-contact__form">
        {view.fields.map((field) => (
          <FormFieldControl
            key={field.name}
            field={field}
            locale={locale}
            fieldClassName="ws-form__field"
            editable={editable}
            activeEditTarget={target}
          />
        ))}
        <label className="ws-form__consent">
          <input type="checkbox" name="consent" required />
          {view.consentFromFields ? (
            <span>{view.consentFromFields.label}</span>
          ) : (
            <EditableContent as="span" fieldKey="consent" editable={editable} activeEditTarget={target}>
              {view.consent || pickChrome('consentFallback', locale)}
            </EditableContent>
          )}
        </label>
        <EditableContent
          as="button"
          type="submit"
          fieldKey="ctaLabel"
          editable={editable}
          activeEditTarget={target}
          className="ws-contact__submit"
        >
          {view.ctaLabel || pickChrome('submitFallbackReservation', locale)}
        </EditableContent>
      </SiteLeadForm>
    </div>
  );
}
